import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getFilmById, getRelatedFilms, getSpecialsForFilm } from "@/utils/film";

export default function DetailPage() {
  const { id } = useParams();
  const film = useMemo(() => getFilmById(id), [id]);
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState("");

  useDocumentMeta(
    film ? `妖精网站 - ${film.title}剧情介绍与信息` : "妖精网站 - 未找到该作品",
    film
      ? `观看${film.title}详细剧情介绍、主演信息及相关妖精影视推荐，尽在妖精网站。`
      : undefined
  );

  if (!film) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <p className="font-heading text-lg font-semibold text-foreground-800">
          未找到该妖精影视作品
        </p>
        <p className="mt-2 text-sm text-foreground-500">它可能已被移除或链接有误</p>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedFilms = getRelatedFilms(film, 5);
  const relatedSpecials = getSpecialsForFilm(film.id);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2400);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，快去分享给同好吧");
    } catch {
      showToast("复制失败，请手动复制浏览器地址");
    }
  };

  const metaList = [
    { label: "类型", value: film.type },
    { label: "地区", value: film.region },
    { label: "语言", value: film.language },
    { label: "年份", value: `${film.year} 年` },
    { label: "时长", value: film.duration },
  ];

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category/${film.type}`}
          className="transition-colors hover:text-primary-600"
        >
          妖精{film.type}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{film.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-10">
        {/* 左栏：封面与基本信息 */}
        <aside>
          <div className="aspect-[2/3] w-full max-w-[300px] overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={film.poster}
              alt={`${film.title}海报`}
              title={`${film.title} 妖精影视作品`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-1 font-heading text-2xl font-bold text-accent-600">
                <i className="ri-star-fill text-accent-500" />
                {film.rating.toFixed(1)}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-500">
                <i className="ri-fire-fill text-accent-500" />
                {film.popularity.toLocaleString()} 热度
              </span>
            </div>
            <dl className="space-y-2.5 text-sm">
              <div className="flex gap-2">
                <dt className="w-14 shrink-0 text-foreground-500">导演</dt>
                <dd className="text-foreground-800">{film.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
                <dd className="text-foreground-800">{film.actors}</dd>
              </div>
              {metaList.map((item) => (
                <div key={item.label} className="flex gap-2">
                  <dt className="w-14 shrink-0 text-foreground-500">{item.label}</dt>
                  <dd className="text-foreground-800">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>

        {/* 右栏：标题、剧情与相关 */}
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {film.title}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">{film.intro}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-link-m" />
                复制链接
              </button>
              <button
                type="button"
                onClick={() => showToast("演示功能：分享至微信（暂未接入真实接口）")}
                aria-label="分享至微信"
                className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-500 text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-wechat-line text-lg" />
              </button>
              <button
                type="button"
                onClick={() => showToast("演示功能：分享至微博（暂未接入真实接口）")}
                aria-label="分享至微博"
                className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-primary-950 transition-colors hover:bg-accent-600"
              >
                <i className="ri-weibo-line text-lg" />
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {film.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                # {tag}
              </Link>
            ))}
          </div>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-600" />
              剧情介绍
            </h2>
            <p
              className={`text-sm leading-relaxed text-foreground-700 ${
                expanded ? "" : "line-clamp-3"
              }`}
            >
              {film.synopsis}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-3 flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              {expanded ? "收起剧情" : "展开完整剧情"}
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </button>
          </section>

          {relatedSpecials.length > 0 && (
            <section className="mt-6 rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-bookmark-3-line text-accent-600" />
                收录于专题
              </h2>
              <div className="flex flex-wrap gap-3">
                {relatedSpecials.map((special) => (
                  <Link
                    key={special.id}
                    to={`/special/${special.id}`}
                    className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-700"
                  >
                    <i className="ri-folder-2-line text-accent-600" />
                    {special.title}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12">
        <SectionHeading
          title="相关推荐"
          subtitle="根据类型、主题与标签为你智能关联的妖精影视作品"
          icon="ri-magic-line"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
          {relatedFilms.map((item) => (
            <FilmCard key={item.id} film={item} showIntro={false} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[100] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}